/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useReactToPrint } from 'react-to-print';
import { GContextTypes } from '../../types';
import { GlobalContext } from '../../contexts';
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
} from '@material-ui/core';
import { dublicateAlert, errorAlert, successAlert } from '../common/funcs';
import PopupLayout from '../shared/PopupLayout';
import TextFieldLocal from '../shared/TextFieldLocal';
import AutoFieldLocal from '../shared/AutoFieldLocal';
import { useEmployees } from '../../hooks';

const statusColors: any = {
  new: '#2196f3',
  reviewing: '#ff9800',
  approved: '#4caf50',
  rejected: '#f44336',
  implemented: '#9c27b0',
};

const PopupUrgentRequest = ({
  open,
  onClose,
  row,
  isNew,
  addAction,
  editAction,
  theme,
  words,
  company,
}: any) => {
  const [saving, setSaving] = useState(false);
  const [emplvalue, setEmplvalue] = useState<any>(null);
  const [requestType, setRequestType] = useState<string>('urgent');
  const [alrt, setAlrt] = useState({ show: false, msg: '', type: undefined });

  const { register, handleSubmit, errors, reset, setValue } = useForm();
  const {
    translate: { isRTL },
  }: GContextTypes = useContext(GlobalContext);

  const { employees } = useEmployees();

  const printRef = useRef<any>();
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: row?.title || 'Request',
    removeAfterPrint: true,
  });

  useEffect(() => {
    if (!isNew && row?._id) {
      setValue('title', row.title);
      setValue('desc', row.desc);
      if (row.employeeId && employees) {
        const empl = employees.find((e: any) => e._id === row.employeeId);
        setEmplvalue(empl);
      }
      if (row.category) {
        setRequestType(row.category);
      }
    }
  }, [row, employees]);

  const onSubmit = async (data: any) => {
    setSaving(true);
    const variables: any = {
      _id: row?._id ? row._id : undefined,
      title: data.title,
      desc: data.desc,
      category: requestType,
      priority: requestType === 'urgent' ? 'urgent' : 'medium',
      employeeId: emplvalue?._id,
      employeeName: emplvalue?.name,
      employeeNameAr: emplvalue?.nameAr,
    };

    const mutate = isNew ? addAction : editAction;
    const mutateName = isNew ? 'createSuggestion' : 'updateSuggestion';
    await apply(mutate, mutateName, variables);
  };

  const apply = async (mutate: any, mutateName: string, variables: any) => {
    try {
      const res = await mutate({ variables });
      if (res?.data?.[mutateName]?.ok === false) {
        await errorAlert(setAlrt, isRTL);
        setSaving(false);
        return;
      }
      closeModal();
      await successAlert(setAlrt, isRTL);
    } catch (error) {
      onError(error);
    }
  };

  const onHandleSubmit = () => {
    handleSubmit(onSubmit)();
  };

  const closeModal = () => {
    onClose();
    setSaving(false);
    setEmplvalue(null);
    setRequestType('urgent');
    reset();
  };

  const onError = async (error: any) => {
    if (error.message.includes('duplicate')) {
      await dublicateAlert(setAlrt, isRTL);
    } else {
      await errorAlert(setAlrt, isRTL);
      reset();
      console.log(error);
    }
  };

  const title = isRTL
    ? isNew
      ? 'إضافة طلب عاجل'
      : 'تعديل طلب عاجل'
    : isNew
      ? 'New Urgent Request'
      : 'Edit Urgent Request';

  return (
    <PopupLayout
      isRTL={isRTL}
      open={open}
      onClose={closeModal}
      title={title}
      onSubmit={onHandleSubmit}
      theme={theme}
      alrt={alrt}
      maxWidth={'md'}
      saving={saving}
      print={!isNew ? handlePrint : undefined}
    >
      <>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextFieldLocal
              name="title"
              label={isRTL ? 'عنوان الطلب' : 'Request Title'}
              register={register}
              errors={errors}
              row={row}
              fullWidth
              mb={0}
              required
            />
          </Grid>

          <Grid item xs={6}>
            <AutoFieldLocal
              name="employee"
              title={isRTL ? 'الموظف' : 'Employee'}
              words={words}
              options={employees}
              value={emplvalue}
              setSelectValue={setEmplvalue}
              isRTL={isRTL}
              fullWidth
            ></AutoFieldLocal>
          </Grid>
          <Grid item xs={6}>
            <FormControl variant="outlined" fullWidth margin="dense">
              <InputLabel>{isRTL ? 'نوع الطلب' : 'Request Type'}</InputLabel>
              <Select
                value={requestType}
                onChange={(e: any) => setRequestType(e.target.value)}
                label={isRTL ? 'نوع الطلب' : 'Request Type'}
              >
                <MenuItem value="regular">
                  {isRTL ? 'طلب عادي' : 'Regular Request'}
                </MenuItem>
                <MenuItem value="urgent">
                  {isRTL ? 'طلب عاجل' : 'Urgent Request'}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextFieldLocal
              name="desc"
              label={isRTL ? 'تفاصيل الطلب' : 'Request Details'}
              register={register}
              errors={errors}
              row={row}
              fullWidth
              multiline
              rows={4}
              mb={0}
            />
          </Grid>
        </Grid>

        {/* Hidden print content */}
        <div style={{ display: 'none' }}>
          <div
            ref={printRef}
            style={{
              padding: 40,
              fontFamily: 'Arial, sans-serif',
              direction: isRTL ? 'rtl' : 'ltr',
            }}
          >
            <Typography
              variant="h5"
              style={{ marginBottom: 24, fontWeight: 'bold' }}
            >
              {isRTL ? 'طلب عاجل' : 'Urgent Request'}
            </Typography>
            <Box
              style={{
                borderBottom: '1px solid #ddd',
                paddingBottom: 16,
                marginBottom: 16,
              }}
            >
              <Typography variant="h6" style={{ marginBottom: 8 }}>
                {row?.title}
              </Typography>
              <Box
                display="flex"
                style={{ gap: 12, flexWrap: 'wrap', marginBottom: 8 }}
              >
                <Chip
                  size="small"
                  label={
                    row?.category === 'urgent'
                      ? isRTL
                        ? 'طلب عاجل'
                        : 'Urgent'
                      : isRTL
                        ? 'طلب عادي'
                        : 'Regular'
                  }
                  style={{
                    backgroundColor:
                      row?.category === 'urgent' ? '#f44336' : '#2196f3',
                    color: '#fff',
                  }}
                />
                <Chip
                  size="small"
                  label={row?.status || 'new'}
                  style={{
                    backgroundColor: statusColors[row?.status] || '#2196f3',
                    color: '#fff',
                  }}
                />
              </Box>
            </Box>
            {(row?.employeeName || row?.employeeNameAr) && (
              <Box style={{ marginBottom: 12 }}>
                <Typography
                  variant="body2"
                  style={{ color: '#555', fontWeight: 'bold' }}
                >
                  {isRTL ? 'الموظف:' : 'Employee:'}
                </Typography>
                <Typography variant="body1">
                  {isRTL ? row?.employeeNameAr : row?.employeeName}
                </Typography>
              </Box>
            )}
            {row?.createdAt && (
              <Box style={{ marginBottom: 12 }}>
                <Typography
                  variant="body2"
                  style={{ color: '#555', fontWeight: 'bold' }}
                >
                  {isRTL ? 'التاريخ:' : 'Date:'}
                </Typography>
                <Typography variant="body1">
                  {new Date(row.createdAt).toLocaleDateString(
                    isRTL ? 'ar' : 'en-GB',
                  )}
                </Typography>
              </Box>
            )}
            {row?.desc && (
              <Box style={{ marginBottom: 12 }}>
                <Typography
                  variant="body2"
                  style={{ color: '#555', fontWeight: 'bold' }}
                >
                  {isRTL ? 'التفاصيل:' : 'Details:'}
                </Typography>
                <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                  {row.desc}
                </Typography>
              </Box>
            )}
          </div>
        </div>
      </>
    </PopupLayout>
  );
};

export default PopupUrgentRequest;
